import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { IChecklistV2, ICheckpointGroup, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { Subject } from 'rxjs';
import { CheckpointEditorFormModelNames, CheckpointEditorTabs } from '@fi-shared/checklist/checklist-enum';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { NgModel } from '@angular/forms';
import { AppTextService } from '@fi-shared/app-text';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiChecklistEditorStoreService } from '@fi-shared/checklist/fi-checklist-editor-store.service';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-checkpoint-edit',
    templateUrl: './checkpoint-edit.component.html',
    styleUrls: ['./checkpoint-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-edit' },
    standalone: false,
})
export class FiCheckpointEditComponent implements OnInit {
    @Input() checklistId: number;
    @Input() checkpointGroupIndex: number;
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() isAddNewCheckpoint: boolean;
    @Input() checkpoint: ICheckpointV2;
    @Input() checklistName: string;
    @HostBinding('class.actions-offset-bottom') @Input() isChatAvailable: boolean;

    isQRCodeEnabled: boolean;
    isComplianceThresholdEnabled: boolean;
    @Input() checkpointIndex: number;

    private _checkpointEditorForm: FiValidatingFormDirective;
    @ViewChild(FiValidatingFormDirective) set checkpointEditorForm(value: FiValidatingFormDirective) {
        if (isDefined(value)) this._checkpointEditorForm = value;
    }

    CheckpointEditorTabs = CheckpointEditorTabs;
    focusInputName$ = new Subject<void>();
    updateConditionTypeOptions$ = new Subject<void>();
    selectedTabIndex = CheckpointEditorTabs.General;
    private fiValuesComparison: FiValuesComparison<ICheckpointV2>;
    private checklist: IChecklistV2;
    private checkpointGroups: ICheckpointGroup[];

    constructor(
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private appTextService: AppTextService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private fiChecklistStoreService: FiChecklistStoreService,
        private fiUserDataService: FiUserDataService,
    ) {}

    ngOnInit(): void {
        this.setTranslationData();
        this.initCheckpointEditorData();
    }

    protected getCheckpointName(): string {
        return this.fiContentLanguageSharedService.getContentLanguageValueForDisplayContent(this.checkpoint.Names);
    }

    private setTranslationData() {
        if (!this.fiChecklistStoreService.availableContentLanguages?.length) {
            this.fiChecklistStoreService.selectedLanguageId = this.fiUserDataService.userData.PreferredContentLanguage;
            this.fiChecklistStoreService.availableContentLanguages =
                this.fiContentLanguageSharedService.getContentLanguages();
        }
    }

    private initCheckpointEditorData() {
        this.checklist = cloneDeep(this.fiChecklistEditorStoreService.getInitialChecklistData());
        this.checkpointGroups = cloneDeep(this.checklist.CheckpointGroups);

        const currentChecklistData: IChecklistV2 = this.fiChecklistEditorStoreService.getCurrentChecklistData$().value;
        this.isQRCodeEnabled = currentChecklistData.IsQRCodeEnabled;
        this.isComplianceThresholdEnabled = currentChecklistData.IsComplianceThresholdEnabled;
    }

    private readonly checkpointGroupsWatcherIndex: number = 0;
    private readonly checklistWatcherIndex: number = 1;
    private readonly checkpointWatcherIndex: number = 2;

    private setSaveChangeWarningWatcher() {
        const objectsFn = new Array(3);
        objectsFn[this.checkpointGroupsWatcherIndex] = () => this.checkpointGroups;
        objectsFn[this.checklistWatcherIndex] = () => this.checklist;
        objectsFn[this.checkpointWatcherIndex] = () => this.checkpoint;
        this.saveChangesWarningService.startForMultipleObjects(objectsFn);
        this.fiValuesComparison = new FiValuesComparison(() => this.checkpoint);
        this.saveChangesWarningService.preventParentStateObjCheck = true;
        this.saveChangesWarningService.parentStateObjIndexes = [
            this.checkpointGroupsWatcherIndex,
            this.checklistWatcherIndex,
        ];
    }

    startChangesWatch() {
        const checkpointCopy: ICheckpointV2 = cloneDeep(this.checkpoint);
        if (this.isNewCheckpoint()) {
            this.checkpoint = this.fiCheckpoint2HelperService.getCheckpointInstanceV2();
        }

        this.setSaveChangeWarningWatcher();

        this.checkpoint = checkpointCopy;
        const currentChecklistData: IChecklistV2 = this.fiChecklistEditorStoreService.getCurrentChecklistData$().value;
        this.checklist = cloneDeep(currentChecklistData);
        this.checkpointGroups = cloneDeep(this.fiChecklistEditorStoreService.getCurrentCheckpointGroups());
    }

    getCheckpointEditText(): string {
        const checkpointName: string = this.fiContentLanguageSharedService.getContentLanguageValueForDisplayContent(
            this.checkpoint.Names,
        );
        return this.appTextService.get('checklist.checkpoint_edit') + ': ' + checkpointName;
    }

    navigateToCheckpointSections() {
        if (!this.fiValuesComparison.isChanged()) {
            this.saveChangesWarningService.stop();
        }

        window.history.back();
    }

    isTranslationCardVisible(): boolean {
        return this.fiChecklistHelperService.isTranslationCardVisible();
    }

    saveCheckpoint() {
        if (this.isNewCheckpoint()) {
            this.checkpointGroups[this.checkpointGroupIndex].Checkpoints.push(this.checkpoint);
        } else {
            this.checkpointGroups[this.checkpointGroupIndex].Checkpoints[this.checkpointIndex] = this.checkpoint;
        }
        this.fiChecklistEditorStoreService.setCurrentCheckpointGroups(this.checkpointGroups);
        this.saveChangesWarningService.stop();
        window.history.back();
    }

    private isNewCheckpoint(): boolean {
        return this.checkpointIndex === -1;
    }

    onCheckpointRangeChange() {
        this.updateConditionTypeOptions$.next();
    }

    submitCheckpointForm() {
        this._checkpointEditorForm.submit();
    }

    handleCheckpointEditorFormError(invalidModels: NgModel[]) {
        const isTabInvalid = (tabModelNames: string[]) => {
            return invalidModels.some((invalidModel: NgModel) => tabModelNames.includes(invalidModel.name));
        };

        const checkpointGeneralTabModelNames: string[] = [
            CheckpointEditorFormModelNames.Name,
            CheckpointEditorFormModelNames.Type,
            CheckpointEditorFormModelNames.MeasureUnit,
            CheckpointEditorFormModelNames.Scoring,
            CheckpointEditorFormModelNames.Ranges,
        ];

        if (isTabInvalid(checkpointGeneralTabModelNames)) {
            this.selectedTabIndex = CheckpointEditorTabs.General;
            return;
        }

        const checkpointConditionsTabModelNames: string[] = [
            CheckpointEditorFormModelNames.ConditionType,
            CheckpointEditorFormModelNames.ConditionTargetType,
        ];

        if (isTabInvalid(checkpointConditionsTabModelNames)) {
            this.selectedTabIndex = CheckpointEditorTabs.Conditions;
            return;
        }
    }
}
